/**
 * The keyboard end of the Note menu: playing notes in rather than typing them.
 *
 * It is a piece of the Note menu and not a menu of its own, because what it does is write
 * notes and that is what the Note menu is. It is in its own file because it is the one part
 * of the bar that depends on a device being there: with no Web MIDI, or before the user has
 * asked for it, the whole section is one item.
 */

import type { ScoreMidi } from "./use-score-midi";
import { RULE } from "./score-menu-model";
import type { MenuEntry } from "./score-menu-model";

/** The items that attach, pick or drop a MIDI keyboard, ready to append to a menu. */
export function keyboardItems(midi: ScoreMidi): readonly MenuEntry[] {
  if (!midi.asked) {
    return [
      RULE,
      {
        label: "Play notes in from a MIDI keyboard…",
        // The item is shown and refused rather than hidden: a player looking for it should
        // find out that the browser has no MIDI, not that this editor has no such feature.
        disabled: !midi.possible,
        run: midi.ask,
      },
    ];
  }

  if (midi.keyboards.length === 0) {
    return [RULE, { label: "No MIDI keyboard is attached", disabled: true, run: () => undefined }];
  }

  // Ticked rather than listed under a heading, and clicking the ticked one stops listening:
  // one keyboard writes at a time, which is what "the keyboard" means at a desk.
  return [
    RULE,
    ...midi.keyboards.map(
      (keyboard): MenuEntry => ({
        label: `Play in from ${keyboard.name}`,
        checked: midi.listening === keyboard.id,
        run: () => midi.listen(midi.listening === keyboard.id ? null : keyboard.id),
      }),
    ),
  ];
}
